import { FC, ReactNode, useContext, useEffect, useState } from "react";
import { Box, alpha, lighten, useTheme } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header/Header";
import PaymentSuccessBanner from "src/content/banners/PaymentSuccessPage";
import PaymentCancelBanner from "src/content/banners/PaymentCancelBanner";
import { SidebarContext } from "src/contexts/SidebarContext";
import UpgradeYourPlanBanner from "src/content/banners/UpgradeYourPlanBanner";
import IncreaseLimitsBanner from "src/content/banners/IncreaseLimitsBanner";

interface SidebarLayoutProps {
  children?: ReactNode;
}

const SidebarLayout: FC<SidebarLayoutProps> = () => {
  const theme = useTheme();
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showCancelBanner, setShowCancelBanner] = useState(false);

  const { remainingRequests, showUpgradeBanner, setShowUpgradeBanner, handleUpgrade, showIncreaseLimitsBanner, setShowIncreaseLimitsBanner } = useContext(SidebarContext);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname.includes("/dashboard/home/success")) {
      //Open Success banner
      setShowSuccessBanner(true);
      setShowCancelBanner(false);
      setShowUpgradeBanner(false);

      // Go to /dashboard/home after displaying the Success banner (and close it)
      navigate("/dashboard/home", { replace: true });
    }

    if (location.pathname.includes("/dashboard/home/cancel")) {
      //Open Cancel banner
      setShowCancelBanner(true);
      setShowSuccessBanner(false);
      setShowUpgradeBanner(false);

      // Go to /dashboard/home after displaying the Cancell banner (and close it)
      navigate("/dashboard/home", { replace: true });
    }
  }, [location, navigate, setShowUpgradeBanner]);

  const handleCloseSuccessBanner = () => {
    setShowSuccessBanner(false);
  };

  const handleCloseCancelBanner = () => {
    setShowCancelBanner(false);
  };

  const handleCloseUpgradeBanner = () => {
    setShowUpgradeBanner(false);
  };

  const handleCloseIncreaseLimitsBanner = () => {
    setShowIncreaseLimitsBanner(false);
  };

  const handleBannerUpgrade = () => {
    handleUpgrade();
  };

  return (
    <>
      {/* Successful payment banner */}
      {showSuccessBanner && <PaymentSuccessBanner onClose={handleCloseSuccessBanner} />}

      {/* Cancellation banner */}
      {showCancelBanner && <PaymentCancelBanner onClose={handleCloseCancelBanner} />}

      {/* Upgrade plan banner */}
      {showUpgradeBanner && <UpgradeYourPlanBanner onClose={handleCloseUpgradeBanner} onUpgrade={handleBannerUpgrade} />}

      {/* Increase limits banner plan banner */}
      {showIncreaseLimitsBanner && <IncreaseLimitsBanner onClose={handleCloseIncreaseLimitsBanner} />}

      <Box
        sx={{
          flex: 1,
          height: "100%",

          ".MuiPageTitle-wrapper": {
            background: theme.palette.mode === "dark" ? theme.colors.alpha.trueWhite[5] : theme.colors.alpha.white[50],
            marginBottom: `${theme.spacing(4)}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 1px 0 ${alpha(lighten(theme.colors.primary.main, 0.7), 0.15)}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
                : `0px 2px 4px -3px ${alpha(theme.colors.alpha.black[100], 0.1)}, 0px 5px 12px -4px ${alpha(theme.colors.alpha.black[100], 0.05)}`,
          },
        }}
      >
        <Header />
        <Sidebar />
        <Box
          sx={{
            position: "relative",
            zIndex: 5,
            display: "block",
            flex: 1,
            pt: `${theme.header.height}`,
            [theme.breakpoints.up("lg")]: {
              ml: `${theme.sidebar.width}`,
            },
          }}
        >
          <Box display="block">
            <Outlet />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SidebarLayout;
